import React, { Fragment } from 'react';
import PropTypes from 'prop-types';
import Validator from '../../../common/forms/Validator';
import ActionsGroup from '../../../common/ui/ItemActions';
import ErrorMessage from '../../../common/forms/ErrorMessage';
import Loader from '../../../common/ui/Loader';
import { extractPaymentCardData, visuallyFormatCardNumber, getCardNumberFieldMaxLength } from '../../../../helpers/companies';
import { creditCardIconSource } from '../../../../images/assets';
import { creditCardBrands, paymentFieldsMaxLengths } from '../../../../constants/companies';
import { alertMessages } from '../../../../constants/text';
import UsersService from '../../../../services/UsersService';
import Edit from '../../../common/forms/buttons/Edit';
import Delete from '../../../common/forms/buttons/Delete';

export default class Payment extends Validator {
  static propTypes = {
    company: PropTypes.object.isRequired,
    getCompanyPaymentAction: PropTypes.func.isRequired,
    createStripeCustomerWithCard: PropTypes.func.isRequired,
    UIShowConfirm: PropTypes.func.isRequired,
    deletePaymentInfo: PropTypes.func.isRequired
  };

  validationRules = {
    name: { required: true },
    cardNumber: { required: true },
    month: { required: true },
    year: { required: true },
    cvc: { required: true }
  };

  state = {
    isLoading: true,
    name: '',
    cardNumber: '',
    month: '',
    year: '',
    cvc: '',
    cardFormIsShown: false
  };

  componentDidMount () {
    const { company: { id } } = this.props;
    this.props.getCompanyPaymentAction(id).then(this._setDataLoaded);
  }

  _setDataLoaded = () => {
    this.setState({ isLoading: false });
  };

  showNewCardForm = () => {
    this.setState({
      cardFormIsShown: true
    });
  };

  removeCard = () => {
    this.props.UIShowConfirm(
      'Are you sure you want to delete this credit card?',
      () => {
        const { company: { id, payment }, deletePaymentInfo, getCompanyPaymentAction } = this.props;
        this.setState({ isLoading: true });
        deletePaymentInfo(payment.id).then(() => {
          getCompanyPaymentAction(id).then(this._setDataLoaded);
        });
      }
    );
  };

  closeNewCardForm = () => {
    this.setState({
      cardFormIsShown: false,
      name: '',
      cardNumber: '',
      month: '',
      year: '',
      cvc: ''
    });
    this._resetErrors();
  };

  submitAddingCard = e => {
    e.preventDefault();
    if (this.validate(this.validationRules)) {
      const { name, cardNumber, month, year, cvc } = this.state;
      const { company: { id, address, state, city, zip }, createStripeCustomerWithCard } = this.props;
      this.setState({ isLoading: true });
      createStripeCustomerWithCard(id, { name, cardNumber, month, year, cvc, address, city, state, zip })
        .then(data => {
          this._setDataLoaded();
          if (data) {
            this.closeNewCardForm();
          }
        });
    }
  };

  handleChange = e => {
    const { name, maxLength } = e.target;
    let value = e.target.value;
    if (maxLength !== -1 && value.length > maxLength) return;
    if (name === 'cardNumber') {
      value = visuallyFormatCardNumber(value);
    }
    if (name === 'month') {
      this.validationRules.month = value ? { maxValue: 12, minValue: 1 } : { required: true };
    }
    this.setState({
      [name]: value
    });
  };

  render () {
    if (this.state.isLoading) {
      return <Loader />;
    }
    const { company: { is_cc_exempt, payment } } = this.props;
    const { name, cardNumber, month, year, cvc, cardFormIsShown } = this.state;
    const errors = this.validationErrors;
    let cardData;
    if (payment) {
      cardData = extractPaymentCardData(payment.cardInfo);
    }
    const isAdmin = UsersService.isAdmin();
    return (
      <Fragment>
        { is_cc_exempt && (
          <Fragment>
            { isAdmin ? (
              <div className='alert alert-danger alert-static' id='is-cc-exempt-static-alert'>
                { alertMessages.noCConFilePayment.admin }
              </div>
            ) : (
              <Fragment>
                <h4 className='mb-1'>Invoice</h4>
                { alertMessages.noCConFilePayment.employer }
              </Fragment>
            )}
          </Fragment>
        )}
        { cardData && (!is_cc_exempt || isAdmin) ? (
          <div className='d-flex justify-content-between'>
            <figure className='d-flex align-center'>
              <div
                className='credit-card-icon'
                style={{ backgroundImage: `url('${creditCardIconSource[creditCardBrands[cardData.brand.toLowerCase()]]}')` }}
              />
              <div>
                <p>Card Ending in: {cardData.last4}</p>
                <p>Exp. Date: {cardData.exp_month}/{cardData.exp_year}</p>
              </div>
            </figure>
            { !cardFormIsShown && (
              <div>
                <Edit onClick={this.showNewCardForm} />
                { isAdmin && <Delete onClick={this.removeCard} /> }
              </div>
            )}
          </div>
        ) : (
          <Fragment>
            {!is_cc_exempt && (
              <button
                className='button button-primary'
                onClick={this.showNewCardForm}
              >
                Add New Card
              </button>
            )}
          </Fragment>
        )}
        { cardFormIsShown && (
          <div className='row'>
            <form className='col-lg-6' onSubmit={this.submitAddingCard}>
              <div className='payment-icons-wrapper'>
                <ActionsGroup
                  isLoading={false}
                  isEditing
                  exitEditMode={this.closeNewCardForm}
                />
              </div>
              <div className='form-group'>
                <label htmlFor='cardholder-name'><strong>Cardholder Name:</strong></label>
                <input
                  id='cardholder-name'
                  className='form-control form-control-sm'
                  name='name'
                  value={name || ''}
                  onChange={this.handleChange} />
                <ErrorMessage errorText={errors.name} />
              </div>

              <div className='form-group'>
                <label htmlFor='card-number'><strong>Card Number:</strong></label>
                <input
                  id='cardholder-name'
                  className='form-control form-control-sm'
                  name='cardNumber'
                  value={cardNumber || ''}
                  onChange={this.handleChange}
                  maxLength={getCardNumberFieldMaxLength(cardNumber)}
                />
                <ErrorMessage errorText={errors.cardNumber} />
              </div>
              <div className='d-flex justify-content-between month-year-cvv-wrapper'>
                <div className='form-group'>
                  <label htmlFor='exp-month'><strong>Exp.Month:</strong></label>
                  <input
                    id='exp-month'
                    className='form-control form-control-sm'
                    type='number'
                    name='month'
                    value={month || ''}
                    onChange={this.handleChange}
                    maxLength={paymentFieldsMaxLengths.month}
                    placeholder='MM'
                  />
                  <ErrorMessage errorText={errors.month} />
                </div>
                <div className='form-group'>
                  <label htmlFor='exp-year'><strong>Exp.Year:</strong></label>
                  <input
                    id='exp-year'
                    className='form-control form-control-sm'
                    type='number'
                    name='year'
                    value={year || ''}
                    onChange={this.handleChange}
                    maxLength={paymentFieldsMaxLengths.year}
                    placeholder='YYYY'
                  />
                  <ErrorMessage errorText={errors.year} />
                </div>
                <div className='form-group'>
                  <label htmlFor='cvc'><strong>CVV:</strong></label>
                  <input
                    id='cvc'
                    className='form-control form-control-sm'
                    type='number'
                    name='cvc'
                    value={cvc || ''}
                    onChange={this.handleChange}
                    maxLength={paymentFieldsMaxLengths.cvc}
                  />
                  <ErrorMessage errorText={errors.cvc} />
                </div>
              </div>
            </form>
          </div>
        )}
      </Fragment>
    );
  }
}



// WEBPACK FOOTER //
// ./src/components/app/companyDetails/Tabs/Payment.js